import { useCallback, useSyncExternalStore } from 'react';

/**
 * Whether the rail is folded to icons-only.
 *
 * Defaults to expanded: the whole point is that ProManage, the organisation, and the signed-in
 * user are visible without opening a menu. Collapsing is an explicit choice, kept per browser
 * rather than per organisation — it is a preference about screen space, not about the account.
 */
export const STORAGE_KEY = 'promanage:rail-collapsed';

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // Private browsing or storage disabled — not an error, just no stored preference.
    return false;
  }
}

// ── Store ────────────────────────────────────────────────────────────────────
// Same tiny external-store shape as lib/theme.ts: read in a handful of places, changes rarely,
// and keeping it out of the React tree means a route change cannot reset it mid-session.

let collapsed = false;
let initialised = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function init() {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;
  collapsed = readStored();

  // Keep tabs in step, the same reason lib/theme.ts does — collapsing the rail in one tab and
  // finding the other still wide is a small but persistent papercut.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    collapsed = event.newValue === '1';
    emit();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

if (typeof window !== 'undefined') init();

export function setRailCollapsed(next: boolean): void {
  collapsed = next;
  try {
    localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  } catch {
    // Preference simply will not persist; the current session still works.
  }
  emit();
}

/** `[collapsed, setCollapsed]`. */
export function useRailCollapsed(): [boolean, (next: boolean) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => collapsed,
    () => false, // server/prerender snapshot
  );

  const set = useCallback((next: boolean) => setRailCollapsed(next), []);
  return [value, set];
}
